import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Input, Select, Typography, Button } from 'antd';
// Api
import { UPDATE_SOCIAL_BOOST_CAMPAIGN } from 'api/campaignV2/mutations';
import { SEARCH_SOCIAL_BOOST_CAMPAIGN_STORES } from 'api/campaignV2/queries';
// Helpers
import { getLocationsForApi } from 'helpers/campaignEditModal';
import {
  getSocialBoostCampaignCommonFields,
  getSocialBoostCampaignInitialFormValues,
} from 'helpers/campaignSocialBoost';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignActivityType,
  CampaignLocationInput,
  CampaignSmPlatform,
  CampaignStartMode,
  SearchSocialBoostCriteriaInput,
  SocialBoostActivityInput,
  SortDirection,
  StoreGender,
  UpdateSocialBoostCampaignInput,
  UserRole,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
} from 'api/graphql-global-types';
import {
  SearchSocialBoostCampaignStores,
  SearchSocialBoostCampaignStoresVariables,
} from 'api/campaignV2/types/SearchSocialBoostCampaignStores';
import {
  UpdateSocialBoostCampaign,
  UpdateSocialBoostCampaignVariables,
} from 'api/campaignV2/types/UpdateSocialBoostCampaign';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
import CampaignDetails from 'components/common/CampaignEditComponents/CampaignDetails/CampaignDetails';
import CompanyDetails from 'components/common/CampaignEditComponents/CompanyDetails/CompanyDetails';
// Style
import styles from './SocialBoostCampaignEditModal.module.scss';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export type SocialBoostCampaignFormValues = {
  userRole: UserRole[];
  budget: number;
  direction?: SortDirection | null;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  locations?: CampaignLocationInput[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  smPlatforms: CampaignSmPlatform[];
  followingFrom: number | null;
  followingTo: number | null;
  sports: string[];
  brandName: string;
  website: string;
  campaignName: string;
  internalName: string;
  dueDate: any;
  postUrl: string;
  caption: string;
  tags: string;
  linkStickerUrl: string;
  linkStickerTitle: string;
  startMode: CampaignStartMode;
  startAt: any;
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  engagementRate?: { value: number; label: string } | null;
  searchHashtags?:
    | { label: string; value: string; storeCount: number }[]
    | null;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type SocialBoostCampaignEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const SocialBoostCampaignEditModal = ({
  record,
  onFinish,
}: SocialBoostCampaignEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const searchCriteria =
    record?.search as GetCampaignsV2_getCampaigns_entities_search;
  const initialPlatform = (searchCriteria as any)?.platformCriteria?.[0]
    ?.platform as CampaignSmPlatform | undefined;
  const initialSmPlatforms: CampaignSmPlatform[] = initialPlatform
    ? [initialPlatform]
    : [];

  const [isInstagram, setIsInstagram] = useState<boolean>(
    initialSmPlatforms.includes(CampaignSmPlatform.IG)
  );
  const [isTikTok, setIsTikTok] = useState<boolean>(
    initialSmPlatforms.includes(CampaignSmPlatform.TT)
  );
  const [boostStartMode, setBoostStartMode] = useState<CampaignStartMode>(
    record?.startMode ?? CampaignStartMode.ASAP
  );

  const filters = searchCriteria;

  const [
    searchSocialBoostStores,
    { data: storeData, loading: loadingStoreData },
  ] = useLazyQuery<
    SearchSocialBoostCampaignStores,
    SearchSocialBoostCampaignStoresVariables
  >(SEARCH_SOCIAL_BOOST_CAMPAIGN_STORES);

  const [updateSocialBoostCampaign, { loading: loadingUpdateCampaign }] =
    useMutation<UpdateSocialBoostCampaign, UpdateSocialBoostCampaignVariables>(
      UPDATE_SOCIAL_BOOST_CAMPAIGN
    );

  const initialFormValues: SocialBoostCampaignFormValues =
    getSocialBoostCampaignInitialFormValues(record);

  useEffect(() => {
    if (record && filters) {
      form.setFieldsValue(getSocialBoostCampaignInitialFormValues(record));
      setBoostStartMode(record.startMode ?? CampaignStartMode.ASAP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSubmit = async (lockEnabled = true) => {
    const {
      brandName,
      website,
      campaignName,
      internalName,
      postUrl,
      caption,
      tags,
      linkStickerUrl,
      linkStickerTitle,
      startMode,
      startAt,
      locations,
      hometownLocations,
      excludedLocations,
    } = form.getFieldsValue();

    const commonFields = getSocialBoostCampaignCommonFields(
      form.getFieldsValue(),
      isTikTok
    );
    const searchLocations = getLocationsForApi(locations);
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(excludedLocations || []);

    // hashtagInputs are already built (from the `label`) by
    // getSocialBoostCampaignCommonFields above.
    const criteria: SearchSocialBoostCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
    };

    const tagsArray = tags
      ? tags
          .split(',')
          .map((t: string) => t.trim())
          .filter(Boolean)
      : [];

    const socialBoost = {
      ...(brandName && { brandName }),
      ...(website && { website }),
      ...(postUrl && { postUrl }),
      ...(caption && { caption }),
      ...(tagsArray.length && { tags: tagsArray }),
      ...(isInstagram && linkStickerUrl && { linkStickerUrl }),
      ...(isInstagram && linkStickerTitle && { linkStickerTitle }),
    };

    const activities: SocialBoostActivityInput[] = [];

    if (Object.keys(socialBoost).length > 0) {
      activities.push({
        socialBoost,
        type: CampaignActivityType.SocialBoost,
      });
    }

    const updateCampaignInputVariables: UpdateSocialBoostCampaignInput = {
      id: record?.id || '',
      ownerId: record?.agency?.id,
      criteria,
      ...(activities.length > 0 && { activities }),
      ...(campaignName && { name: campaignName }),
      internalName: internalName || campaignName,
      lockEnabled,
      ...(startMode && { startMode }),
      // startAt is a local datetime-local value; send it as a UTC ISO string
      // (same as Campaign Builder) so the stored instant round-trips correctly.
      ...(startMode === CampaignStartMode.SCHEDULED &&
        startAt && { startAt: new Date(startAt).toISOString() }),
    };

    try {
      const { data } = await updateSocialBoostCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateSocialBoostCampaign) {
        successNotification('The campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleFilterSearch = async (
    criteria: SearchSocialBoostCriteriaInput
  ) => {
    try {
      searchSocialBoostStores({
        variables: {
          input: {
            limit: 4,
            offset: 0,
            criteria,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleFilterClick = async () => {
    const commonFields = getSocialBoostCampaignCommonFields(
      form.getFieldsValue(),
      isTikTok
    );
    const searchLocations = getLocationsForApi(commonFields?.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    // hashtagInputs are already built (from the `label`) by
    // getSocialBoostCampaignCommonFields above.
    const criteria: SearchSocialBoostCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
    };

    handleFilterSearch(criteria);
  };

  const talentsData = storeData?.searchSocialBoostCampaignStores?.entities;

  const hasMore =
    (talentsData?.length ?? 0) <
    (storeData?.searchSocialBoostCampaignStores.total ?? 0);

  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;

  const totalFollowers = Number(
    storeData?.searchSocialBoostCampaignStores.summary.followers
  );
  const totalTalents = Number(storeData?.searchSocialBoostCampaignStores.total);

  useEffect(() => {
    const commonFields = getSocialBoostCampaignCommonFields(
      initialFormValues,
      isTikTok
    );
    const searchLocations = getLocationsForApi(commonFields?.locations || []);

    const criteria: SearchSocialBoostCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
    };

    handleFilterSearch(criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLoading = loadingUpdateCampaign || loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditSocialBoostCampaignForm"
        aria-label="Update Social Boost Campaign"
        layout="vertical"
        onFinish={() => handleSubmit(true)}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          <CampaignCriteria
            form={form}
            initialFormValues={initialFormValues}
            record={record}
            isInstagram={isInstagram}
            setIsInstagram={setIsInstagram}
            isTwitter={false}
            setIsTwitter={() => undefined}
            isTikTok={isTikTok}
            setIsTikTok={setIsTikTok}
            setSelectedTasks={() => undefined}
            handleFilterClick={handleFilterClick}
            loadingStoreData={loadingStoreData}
            talentsData={talentsData}
            hasMore={hasMore}
            totalTalents={totalTalents}
            totalFollowers={totalFollowers}
            showTalentList={showTalentList}
            canEditPremiumFilters={canEditPremiumFiltersValue}
            showFlatFeePerStore={false}
          />

          <CampaignDetails record={record} showInstructions={false} />

          <CompanyDetails
            renderCustomFields={() => (
              <div>
                <Form.Item
                  name="startMode"
                  label={<Text>Boost Start Mode</Text>}
                >
                  <Select
                    onChange={(val: CampaignStartMode) =>
                      setBoostStartMode(val)
                    }
                  >
                    <Option value={CampaignStartMode.ASAP}>ASAP</Option>
                    <Option value={CampaignStartMode.SCHEDULED}>
                      Scheduled
                    </Option>
                  </Select>
                </Form.Item>

                {boostStartMode === CampaignStartMode.SCHEDULED && (
                  <Form.Item
                    name="startAt"
                    rules={[
                      {
                        required: true,
                        message:
                          'Boost start date is required for scheduled mode',
                      },
                    ]}
                    label={<Text>Boost Start Date</Text>}
                  >
                    <Input type="datetime-local" />
                  </Form.Item>
                )}
              </div>
            )}
          />

          <div className={styles.heading}>Post Details:</div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="postUrl"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              label={<Text>Post URL</Text>}
            >
              <Input
                type="text"
                placeholder="https://example.link"
                aria-label="Post URL"
              />
            </Form.Item>

            <Form.Item name="caption" label={<Text>Caption</Text>}>
              <TextArea
                autoSize={{ minRows: 3 }}
                placeholder="Suggested caption for the post (max 2200 chars)"
                maxLength={2200}
              />
            </Form.Item>
          </div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="tags"
              label={<Text>Tags (comma-separated @-tags)</Text>}
            >
              <Input
                type="text"
                placeholder="@brand, @partner"
                aria-label="Tags"
              />
            </Form.Item>
          </div>

          {/* Link sticker is an Instagram-only feature. */}
          {isInstagram && (
            <div className={styles.rowWrapper}>
              <Form.Item
                name="linkStickerUrl"
                label={<Text>Link Sticker URL</Text>}
              >
                <Input
                  type="text"
                  placeholder="https://link.com"
                  aria-label="Link Sticker URL"
                />
              </Form.Item>

              <Form.Item
                name="linkStickerTitle"
                label={<Text>Link Sticker Title</Text>}
              >
                <Input
                  type="text"
                  placeholder="Shop Now"
                  aria-label="Link Sticker Title"
                  maxLength={200}
                />
              </Form.Item>
            </div>
          )}
        </div>

        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={() => handleSubmit(false)}
          >
            Save as Draft
          </Button>
          <Button type="primary" htmlType="submit" loading={submitLoading}>
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SocialBoostCampaignEditModal;
